/**
 * «Кто сейчас на объекте» — из ленты проходов за сегодня.
 *
 * ## Почему это считается, а не спрашивается
 *
 * Такого маршрута нет: сервер знает проходы, а не присутствие. Присутствие выводится
 * из последнего события человека за сутки и из того, в какую сторону оно было —
 * `gate_mode` терминала, который его записал.
 *
 * ## Чего этот вывод не может, и это не починить на клиенте
 *
 * ⚠️ **Терминал в режиме «оба» направления не сообщает.** У такого прохода
 * `gate_mode: 'both'`, и по нему нельзя сказать, вошёл человек или вышел, — только
 * что он тут был. Это не пробел в данных и не баг: одна дверь, работающая на вход и
 * на выход, физически не различает их. Поэтому статусов ТРИ, а не два, и третий —
 * «был сегодня» — обязан существовать; свести его к «внутри» значило бы сказать
 * владельцу, что человек на объекте, когда он ушёл два часа назад.
 *
 * Отсюда же правило для UI: если хотя бы одна строка «был сегодня», об этом надо
 * сказать словами — иначе счётчик «внутри: 3» читается как полный ответ, а он
 * неполный ровно настолько, сколько дверей стоит в режиме «оба».
 *
 * ## Что в расчёт не берётся
 *
 * Отказы — вообще: неопознанный человек не имеет карточки, а `record_id` у такой
 * записи пуст, так что отнести её некому. Запрещённый проход опознанного человека
 * тоже никого никуда не перемещает — дверь не открылась.
 */
import type {AccessLogRecord} from '@/entities/log/types';

export type PresenceStatus =
  /** Последним был вход. */
  | 'inside'
  /** Последним был выход. */
  | 'left'
  /** Проходил, но дверь не различает направления. */
  | 'seen';

export type PresenceRow = {
  recordId: string;
  name: string;
  employeeNumber: string | null;
  status: PresenceStatus;
  /** Первое событие за сутки — «на объекте с 09:14». */
  firstMillis: number;
  /** Последнее событие: время и дверь. */
  lastMillis: number;
  lastDeviceName: string;
  events: number;
};

/** Направление последнего прохода → статус. Незнакомый режим — «был», не «внутри». */
function statusFor(gateMode: string | null | undefined): PresenceStatus {
  switch (gateMode) {
    case 'entry':
      return 'inside';
    case 'exit':
      return 'left';
    default:
      return 'seen';
  }
}

/** Внутри — сверху: список открывают ради них. Дальше «был», и последними ушедшие. */
const ORDER: Record<PresenceStatus, number> = {inside: 0, seen: 1, left: 2};

/**
 * Лента за сутки → по строке на человека.
 *
 * Порядок событий на входе не важен: строка собирается по минимуму и максимуму
 * времени, а не по позиции в массиве. Это нарочно — порядок ленты не является частью
 * контракта маршрута (см. `entities/log/day.ts`), и опираться на него здесь значило бы
 * второй раз поставить на одно и то же необещанное свойство.
 */
export function buildPresence(events: readonly AccessLogRecord[]): PresenceRow[] {
  const byPerson = new Map<string, {first: AccessLogRecord; last: AccessLogRecord; count: number}>();

  for (const event of events) {
    if (!event.granted || !event.record_id) {
      continue;
    }
    const current = byPerson.get(event.record_id);
    if (!current) {
      byPerson.set(event.record_id, {first: event, last: event, count: 1});
      continue;
    }
    current.count += 1;
    if (event.created_at_millis < current.first.created_at_millis) {
      current.first = event;
    }
    if (event.created_at_millis > current.last.created_at_millis) {
      current.last = event;
    }
  }

  const rows: PresenceRow[] = [];
  for (const [recordId, {first, last, count}] of byPerson) {
    rows.push({
      recordId,
      name: last.person_name,
      employeeNumber: last.employee_number ?? null,
      status: statusFor(last.gate_mode),
      firstMillis: first.created_at_millis,
      lastMillis: last.created_at_millis,
      lastDeviceName: last.device_name,
      events: count,
    });
  }

  return rows.sort((a, b) => {
    const byStatus = ORDER[a.status] - ORDER[b.status];
    // Внутри одного статуса — свежие сверху: последний прошедший интереснее всех.
    return byStatus !== 0 ? byStatus : b.lastMillis - a.lastMillis;
  });
}

/** Сколько человек в каждом состоянии — счётчики над списком. */
export function presenceCounts(rows: readonly PresenceRow[]): Record<PresenceStatus, number> {
  return rows.reduce(
    (acc, row) => ({...acc, [row.status]: acc[row.status] + 1}),
    {inside: 0, left: 0, seen: 0} as Record<PresenceStatus, number>,
  );
}
